import * as React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import SwitchButton from 'components/common/SwitchButton';
import { getTreeOfContentVisibility } from 'store/treeOfContent/selectors';
import * as actions from 'store/popup/actions';
import { localize } from 'core/localization';
import { POPUP_ID_IMAGE_PREVIEW } from 'constants/popups';
import { KEYS, TAB_INDICES } from 'constants/common';
import { RootAppState } from 'store/types';
import { addAriaLive } from '../../../utils/dom';
import {
  ListItemsBlock,
  ListItem,
  ZoomIconButton,
  ZoomIconBg,
  ZoomIcon,
  HiddenContent
} from './MultipleChoiceItems.styled';
import PreviewGallery from '../../popups/previewGallery/PreviewGallery';
import { SWITCH_BUTTON_LAYOUT } from '../../../constants/components';

const INPUT_TYPE = {
  multiple: {
    checked: 'checked',
    unchecked: 'unchecked'
  },
  single: {
    selected: 'selected',
    notSelected: 'not selected'
  }
};

const keyPrefix = 'list-item-';

export type MultipleChoiceItemsProps = {
  items: any[];
  id: string;
  isMultiple: boolean;
  imageQuestion: boolean;
  selectItem(id: string, itemId?: string, isMultiple?: boolean): void;
  actions: { [key: string]: any };
  isTreeOfContentExpanded: boolean;
  isAnswered?: boolean;
  ariaDisabled: boolean;
  tabIndex: number;
};

const getMultipleChoiceQuestion: any = () => document.getElementById('multipleChoiceQuestion');
export class MultipleChoiceItems extends React.Component<MultipleChoiceItemsProps, {}> {
  state = {
    items: this.props.items,
    isOpened: false,
    imageUrl: [],
    focusedRadioItemId: false,
    isSingleSelectText: !this.props.isMultiple && !this.props.imageQuestion
  };

  static defaultProps = {
    imageQuestion: false,
    ariaDisabled: false
  };

  componentDidMount() {
    const obj = {
      id: 'multipleChoiceQuestion',
      attributes: [
        { key: 'aria-live', value: 'assertive' },
        { key: 'data-test', value: 'multiple-choice-question-aria-live' }
      ]
    };
    addAriaLive(obj);
    this.selectHiddenRadio();
  }

  selectHiddenRadio = () => {
    const { isSingleSelectText } = this.state;
    if (!isSingleSelectText) {
      return;
    }

    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return;
    }

    const radioButton: any = this.getHiddenRadioElement(selectedItem);
    if (!radioButton) {
      return;
    }

    radioButton.checked = true;
  };

  getHiddenRadioElement = (item: any) =>
    document.getElementById(`${keyPrefix}${item.id}`)?.querySelector(`[type='radio']`);

  getAriaLiveAnswer = (selected: boolean, isMultiple: boolean) => {
    if (!selected) {
      return isMultiple ? INPUT_TYPE.multiple.unchecked : INPUT_TYPE.single.notSelected;
    }

    return isMultiple ? INPUT_TYPE.multiple.checked : INPUT_TYPE.single.selected;
  };

  getSelectedItem = () => this.state.items.find(item => item.selected);

  selectItem = (item: any) => {
    const { id, isMultiple, selectItem } = this.props;
    const { items, isSingleSelectText } = this.state;

    if (isMultiple) {
      items.forEach(a => {
        if (item.id === a.id) {
          a.selected = !a.selected;
        }
      });
    } else {
      items.forEach(a => {
        if (item.id === a.id) {
          a.selected = true;
        } else {
          a.selected = false;
        }
      });
    }
    if (typeof selectItem === 'function') {
      selectItem(id, item.id, isMultiple);
    }
    const choiceQuestion = getMultipleChoiceQuestion();
    if (choiceQuestion && choiceQuestion.children.length && !isSingleSelectText) {
      const { text, selected } = item;
      choiceQuestion.children[0].innerHTML = `${text} ${this.getAriaLiveAnswer(
        selected,
        isMultiple
      )}`;
    }
    this.setState({
      items
    });
    this.selectHiddenRadio();
  };

  static getDerivedStateFromProps(
    nextProps: MultipleChoiceItemsProps,
    prevState: MultipleChoiceItemsProps
  ) {
    if (prevState.id !== nextProps.id && prevState.items !== nextProps.items) {
      return {
        items: nextProps.items
      };
    }
    return null;
  }

  showPopup = (imageUrl: string) => {
    this.props.actions.openPopup({
      popupId: POPUP_ID_IMAGE_PREVIEW,
      popupAriaLabelKey: '[aria label gallery popup]',
      component: PreviewGallery,
      imageUrls: [imageUrl],
      disableBottomLine: true
    });
  };

  handleOnClick = (ariaDisabled: boolean, altAnswerImage: string, item: any) => {
    if (!ariaDisabled) {
      if (item.image) {
        item.text = altAnswerImage;
      }
      this.selectItem(item);
    }
  };

  handleOnKeyDown = (event: any, ariaDisabled: boolean, item: any) => {
    if (
      (event.key?.toLowerCase() === KEYS.SPACEBAR.toLowerCase() ||
        event.key?.toLowerCase() === KEYS.ENTER.toLowerCase()) &&
      !ariaDisabled
    ) {
      this.selectItem(item);
    }
  };

  handleRadioChange = (item: any) => (e: any) => {
    if (e.target?.checked) {
      this.selectItem(item);
    }
  };

  handleRadioFocus = (item: any) => () => {
    this.setState({ focusedRadioItemId: item.id });
  };

  handleRadioBlur = (item: any) => () => {
    if (this.state.focusedRadioItemId === item.id) {
      this.setState({ focusedRadioItemId: '' });
    }
  };

  render() {
    let {
      isMultiple,
      imageQuestion,
      isTreeOfContentExpanded,
      ariaDisabled,
      tabIndex,
      isAnswered,
      id: questionId
    } = this.props;
    const { items, focusedRadioItemId, isSingleSelectText } = this.state;
    tabIndex = isSingleSelectText ? TAB_INDICES.DISABLED : tabIndex;
    const disableRadio = isAnswered;

    return (
      <ListItemsBlock
        isImageQuestion={imageQuestion}
        isTreeOfContentExpanded={isTreeOfContentExpanded}
      >
        {items.map((item, index) => {
          const altAnswerImage = item.altText || `${localize('[alt answer image]')} ${index + 1}`;
          return (
            <ListItem
              selected={item.selected}
              focused={focusedRadioItemId === item.id}
              id={`${keyPrefix}${item.id}`}
              key={`${keyPrefix}${index}`}
              isSingleChoice={!isMultiple}
              isImageQuestion={imageQuestion}
              isTreeOfContentExpanded={isTreeOfContentExpanded}
            >
              <SwitchButton
                multiple={isMultiple}
                selected={item.selected}
                hasNoRole={isSingleSelectText}
                text={item.text}
                imageUrl={item.image}
                ariaDisabled={ariaDisabled}
                tabIndex={tabIndex}
                textFor={`radio-${item.id}`}
                layout={
                  imageQuestion ? SWITCH_BUTTON_LAYOUT.IMAGE_ANSWER : SWITCH_BUTTON_LAYOUT.WITH_TEXT
                }
                altAnswerImage={altAnswerImage}
                onClick={() => this.handleOnClick(ariaDisabled, altAnswerImage, item)}
                onKeyDown={(event: any) => this.handleOnKeyDown(event, ariaDisabled, item)}
              />
              {isSingleSelectText && (
                <HiddenContent>
                  <input
                    type="radio"
                    name={questionId}
                    disabled={disableRadio}
                    id={`radio-${item.id}`}
                    onChange={this.handleRadioChange(item)}
                    onFocus={this.handleRadioFocus(item)}
                    onBlur={this.handleRadioBlur(item)}
                  />
                </HiddenContent>
              )}
              <ZoomIconButton
                className="zoom-button"
                ariaLabel={localize('[aria label zoom button]')}
                onClick={() => this.showPopup(item.image)}
                tabIndex={0}
                isShown={!!item.image}
              >
                <ZoomIconBg size={41} name="zoom-bg" />
                <ZoomIcon size={41} name="zoom" />
              </ZoomIconButton>
            </ListItem>
          );
        })}
      </ListItemsBlock>
    );
  }
}

function mapStateToProps(state: RootAppState) {
  return {
    isTreeOfContentExpanded: getTreeOfContentVisibility(state)
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return { actions: bindActionCreators(actions, dispatch) };
}

export default connect(mapStateToProps, mapDispatchToProps)(MultipleChoiceItems);
